import { type SezioneDTO } from '@framework/api-client/api/inquiries/schemas/sezioneDTO.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { apiClient } from '@framework/api-client/client/apiClient.ts';
import { AsyncServicesEngineContextProvider } from '@framework/engine/schema/expressions/repository/async/context/AsyncServicesEngineContext.tsx';
import { AllRootsContextProvider } from '@framework/engine/schema/sections/hooks/AllRootsContext.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { Alert } from 'antd';
import type * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FillableSubmissionContextProvider } from './FillableSubmissionContext.tsx';
import { useAsyncServices } from './hooks/useAsyncServices.tsx';
import { useFillableAllRoots } from './hooks/useFillableAllRoots.tsx';

export interface IEngineFillableContextProviderProps {
  sections: SezioneDTO[];
  children: React.ReactNode;
  /**
   * In caso di compilazione `Frontoffice` è necessario passare la richiesta
   * che si sta compilando.
   */
  submission?: RichiestaDTO | null;

  /** ID Richiesta - Modulo */
  submissionModuleId?: string | null;

  /** Se true, il contesto di compilazione è il Backoffice */
  isBackoffice?: boolean;
}

/**
 * Si occupa di collegare un Form Compilabile (gestito da `@framework/engine`)
 * con il mondo esterno, in particolare con il mondo di `@framework/api-client`.
 *
 * L'engine infatti è _agnostico_ rispetto alle API, ai DTO e al contesto d'uso.
 * Usa solo un `IEngineSection` che è un'astrazione.
 *
 * Questo componente si occupa di collegare l'engine quindi con le API, le sezioni
 * reali, ecc. nella fase di **compilazione**.
 */
export function EngineFillableContextProvider(
  props: IEngineFillableContextProviderProps,
) {
  const { sections, submission, submissionModuleId, isBackoffice } = props;
  const { t } = useTranslation();

  // Servizi asincroni e root. In generale configuriamo qui tutte le
  // informazioni di compilazione per l'Engine
  const {
    services: asyncServices,
    isLoading: isLoadingAsyncServices,
    isError: isAsyncServicesError,
  } = useAsyncServices();
  const allRoots = useFillableAllRoots(sections);

  const services = useMemo(() => asyncServices ?? [], [asyncServices]);

  if (isLoadingAsyncServices) {
    return <LoaderSpin transparent />;
  }

  if (isAsyncServicesError) {
    return (
      <Alert
        showIcon
        type="error"
        message={t('error', 'Errore')}
        description={t(
          'engine.async-services.load-error',
          'Si è verificato un errore durante il caricamento dei servizi esterni. Riprovare.',
        )}
      />
    );
  }

  return (
    <AllRootsContextProvider allRoots={allRoots}>
      <AsyncServicesEngineContextProvider
        axiosClient={apiClient}
        services={services}
      >
        <FillableSubmissionContextProvider
          submission={submission}
          submissionModuleId={submissionModuleId}
          isBackoffice={isBackoffice}
        >
          {props.children}
        </FillableSubmissionContextProvider>
      </AsyncServicesEngineContextProvider>
    </AllRootsContextProvider>
  );
}
